"use client";

import * as React from "react";
import Link from "next/link";
import { motion } from "motion/react";
import {
  BookOpen,
  Sparkles,
  Building2,
  ArrowRight,
  Mic,
  Crown,
  Layers,
  Network,
  Lock,
  Target,
  Shield,
  Compass,
  Cpu,
  Database,
  Server,
} from "lucide-react";
import { PageHero } from "@/components/ui/page-hero";
import { Section } from "@/components/layout/section";
import { RelatedPages } from "@/components/ui/related-pages";
import { ArchitectureDiagramBlock } from "@/lib/blocks/architecture-diagram-block";
import { FeatureGridBlock } from "@/lib/blocks/feature-grid-block";
import { AccordionBlock } from "@/lib/blocks/accordion-block";

const ACCENT: Record<string, { text: string; bg: string; border: string; ring: string; pill: string }> = {
  red: { text: "text-red", bg: "from-red/10", border: "border-red/30", ring: "ring-red/20", pill: "glass-tinted-red border-red/20" },
  purple: { text: "text-purple", bg: "from-purple/10", border: "border-purple/30", ring: "ring-purple/20", pill: "glass-tinted-purple border-purple/20" },
  cyan: { text: "text-cyan", bg: "from-cyan/10", border: "border-cyan/30", ring: "ring-cyan/20", pill: "glass-tinted-cyan border-cyan/20" },
  emerald: { text: "text-emerald", bg: "from-emerald/10", border: "border-emerald/30", ring: "ring-emerald/20", pill: "glass-tinted-emerald border-emerald/20" },
  "amber-gold": { text: "text-amber-gold", bg: "from-amber-gold/10", border: "border-amber-gold/30", ring: "ring-amber-gold/20", pill: "glass-tinted-amber-gold border-amber-gold/20" },
  denim: { text: "text-denim", bg: "from-denim/10", border: "border-denim/30", ring: "ring-denim/20", pill: "glass-tinted-denim border-denim/20" },
  lime: { text: "text-lime", bg: "from-lime/10", border: "border-lime/30", ring: "ring-lime/20", pill: "glass-tinted-lime border-lime/20" },
  coral: { text: "text-coral", bg: "from-coral/10", border: "border-coral/30", ring: "ring-coral/20", pill: "glass-tinted-coral border-coral/20" },
};

export default function DigitalConglomeratePage() {
  return (
    <>
      <PageHero
        icon={BookOpen}
        overline="Glossary · Digital Conglomerate"
        overlineClassName="text-purple"
        title={
          <>
            A Multi-Domain Enterprise That <span className="text-gradient-premium">Owns the Entire Stack</span>
          </>
        }
        subtitle="A digital conglomerate is a multi-domain, technology-integrated enterprise that owns and operates proprietary platforms across interconnected service verticals — rather than licensing third-party tools or advising from the outside. The term describes an entity that commands outcomes across perception, privacy, politics, policing, and policy from a single operational architecture."
        breadcrumbs={[{ label: "Home", href: "/" }, { label: "Glossary", href: "/glossary/" }, { label: "Digital Conglomerate" }]}
        badge="Definition · Organizational form"
      />

      <Section background="default" className="py-16 lg:py-20">
        <div className="max-w-4xl mx-auto">
          <div className={`inline-flex items-center gap-2 px-3 py-1 rounded-full ${ACCENT.purple.pill} mb-5`}>
            <Sparkles className="size-3.5 text-purple" />
            <span className="text-overline text-purple">Formal Definition</span>
          </div>
          <h2 className="text-display-md mb-5">What a digital conglomerate is.</h2>
          <p className="text-body-md text-muted-foreground leading-relaxed mb-5">
            <span className="font-semibold text-foreground">A digital conglomerate</span> is a full-spectrum strategic sovereignty provider that integrates AI-powered intelligence, military-grade security, and political-grade strategy through proprietary platforms across five interconnected domains. Unlike a consulting firm that advises, or a technology vendor that sells products, a digital conglomerate delivers verified results from an integrated operational engine.
          </p>
          <p className="text-body-md text-muted-foreground leading-relaxed mb-5">
            The defining characteristic of a digital conglomerate is ownership. Every platform, every layer of infrastructure, every analytical tool is built in-house and refined through mission-critical deployment. There are no third-party dependencies. There are no licensed engines. Every capability originates from within the organization's own engineering and operational teams.
          </p>
          <p className="text-body-md text-muted-foreground leading-relaxed">
            The digital conglomerate serves governments, royal families, political campaigns, Fortune-scale corporations, and defense agencies across eighteen countries on three continents. The engagement model is ultra-premium — selective, high-stakes, and sovereign-grade.
          </p>
        </div>
      </Section>

      <Section background="muted" className="py-16 lg:py-20">
        <div className="text-center mb-12">
          <div className={`inline-flex items-center gap-2 px-3 py-1 rounded-full ${ACCENT.cyan.pill} mb-4`}>
            <Network className="size-3.5 text-cyan" />
            <span className="text-overline text-cyan">The Distinction</span>
          </div>
          <h2 className="text-display-md mb-4">Digital conglomerate vs. traditional conglomerate.</h2>
          <p className="text-body-md text-muted-foreground max-w-2xl mx-auto">
            A traditional conglomerate diversifies for financial risk reduction. A digital conglomerate integrates for operational compounding.
          </p>
        </div>
        <div className="grid md:grid-cols-2 gap-4">
          {[
            { title: "Traditional Conglomerate", body: "Owns businesses across unrelated industries. Each subsidiary operates independently. Synergies are financial, not operational. Examples include historic industrial conglomerates that held portfolios spanning aviation, healthcare, finance, and consumer goods.", accent: "denim" },
            { title: "Digital Conglomerate", body: "Owns platforms across interconnected domains. The domains — perception, privacy, politics, policing, and policy — share data, intelligence, and learnings across the entire stack. Synergies are operational, not financial. This is the model that produces compounded capabilities.", accent: "purple" },
          ].map((p, i) => {
            const a = ACCENT[p.accent];
            return (
              <motion.div
                key={p.title}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-30px" }}
                transition={{ duration: 0.4, delay: i * 0.06 }}
                className={`glass-card rounded-2xl p-6 bg-gradient-to-br ${a.bg} to-transparent border ${a.border}`}
              >
                <h3 className={`text-heading-md mb-2 ${a.text}`}>{p.title}</h3>
                <p className="text-body-sm text-muted-foreground leading-relaxed">{p.body}</p>
              </motion.div>
            );
          })}
        </div>
      </Section>

      <Section background="default" className="py-16 lg:py-20">
        <div className="text-center mb-12">
          <div className={`inline-flex items-center gap-2 px-3 py-1 rounded-full ${ACCENT["amber-gold"].pill} mb-4`}>
            <Layers className="size-3.5 text-amber-gold" />
            <span className="text-overline text-amber-gold">Nine Proprietary Platforms</span>
          </div>
          <h2 className="text-display-md mb-4">The power measured by the stack.</h2>
          <p className="text-body-md text-muted-foreground max-w-2xl mx-auto">
            The digital conglomerate's power is measured by the breadth and depth of its proprietary technology stack. Each platform serves a specific operational domain and is integrated through a central command interface.
          </p>
        </div>
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3">
          {[
            { tier: "Tier 4", title: "Neural Command Interface", body: "The orchestration hub. 95% coordination success rate. Unifies all sister platforms into a single operational fabric.", icon: Cpu, accent: "red" },
            { tier: "Tier 3", title: "Unified Governance Hub", body: "Integrates five governance pillars: Policy, Police, Pulse, Power, and Public. Over 129 capabilities across government deployments.", icon: Building2, accent: "purple" },
            { tier: "Tier 3", title: "Content Domination Network", body: "Multi-platform distribution engine. 340% average reach increase over organic baseline. 15+ channel coverage.", icon: Network, accent: "cyan" },
            { tier: "Tier 2", title: "AI Digital Intelligence", body: "89% crisis prediction accuracy. Social listening, big data mining, threat detection across 200+ platforms and 100,000+ news sources.", icon: Database, accent: "emerald" },
            { tier: "Tier 2", title: "Perception Proliferation", body: "300-500% narrative penetration improvement. 95-98% organic authenticity rating across 50+ digital platforms.", icon: Sparkles, accent: "amber-gold" },
            { tier: "Tier 2", title: "Ground-Truth Intelligence", body: "87% booth-level prediction accuracy. The platform behind the operational record across political campaigns.", icon: Target, accent: "denim" },
            { tier: "Always Active", title: "Sovereign Security System", body: "Zero-trust architecture. Zero security incidents across all deployments. 99.9999% uptime — maximum 31.5 seconds downtime per year.", icon: Shield, accent: "lime" },
            { tier: "Crisis Response", title: "Crisis Transformation Engine", body: "Sub-5-minute total response time. 384x to 1,416x faster than traditional crisis management approaches.", icon: Target, accent: "coral" },
            { tier: "Governance", title: "Governance Cognition Matrix", body: "127 specialized modules across 9 government function categories. Serving up to 120 million citizens in a single deployment.", icon: Server, accent: "purple" },
          ].map((p, i) => {
            const a = ACCENT[p.accent];
            return (
              <motion.div
                key={p.title}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-30px" }}
                transition={{ duration: 0.4, delay: i * 0.04 }}
                className={`glass-card rounded-2xl p-5 bg-gradient-to-br ${a.bg} to-transparent border ${a.border}`}
              >
                <div className="flex items-center justify-between mb-2">
                  <span className={`text-overline ${a.text}`}>{p.tier}</span>
                  <p.icon className={`size-4 ${a.text}`} />
                </div>
                <h3 className={`text-heading-sm mb-2 ${a.text}`}>{p.title}</h3>
                <p className="text-caption text-muted-foreground leading-relaxed">{p.body}</p>
              </motion.div>
            );
          })}
        </div>
      </Section>

      <Section background="muted" className="py-16 lg:py-20">
        <div className="text-center mb-12">
          <div className={`inline-flex items-center gap-2 px-3 py-1 rounded-full ${ACCENT.emerald.pill} mb-4`}>
            <Lock className="size-3.5 text-emerald" />
            <span className="text-overline text-emerald">The Architectural Commitment</span>
          </div>
          <h2 className="text-display-md mb-4">Zero third-party dependencies.</h2>
          <p className="text-body-md text-muted-foreground max-w-2xl mx-auto">
            The phrase "zero third-party dependencies" is not marketing language. It is an architectural commitment — and it matters for three reasons.
          </p>
        </div>
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {[
            { title: "Security", body: "When a client's intelligence flows through third-party infrastructure, that data passes through systems the client does not control and cannot audit. A digital conglomerate with zero third-party dependencies eliminates this exposure entirely.", accent: "red" },
            { title: "Speed", body: "Third-party integrations introduce latency — API call limits, vendor availability windows, coordination overhead. When every platform is proprietary, optimization happens internally. Sub-100 millisecond coordination. Sub-hour activation.", accent: "amber-gold" },
            { title: "Reliability", body: "A digital conglomerate achieves 99.9999% uptime because the security layer controls its own hardware, its own software, its own update cycle. A third-party vendor's SLA is aspirational. A digital conglomerate controls its own fate.", accent: "emerald" },
          ].map((p, i) => {
            const a = ACCENT[p.accent];
            return (
              <motion.div
                key={p.title}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-30px" }}
                transition={{ duration: 0.4, delay: i * 0.06 }}
                className={`glass-card rounded-2xl p-6 bg-gradient-to-br ${a.bg} to-transparent border ${a.border}`}
              >
                <h3 className={`text-heading-md mb-2 ${a.text}`}>{p.title}</h3>
                <p className="text-body-sm text-muted-foreground leading-relaxed">{p.body}</p>
              </motion.div>
            );
          })}
        </div>
      </Section>

      <Section background="default" className="py-16 lg:py-20">
        <div className="text-center mb-12">
          <div className={`inline-flex items-center gap-2 px-3 py-1 rounded-full ${ACCENT.cyan.pill} mb-4`}>
            <Network className="size-3.5 text-cyan" />
            <span className="text-overline text-cyan">Related Terms</span>
          </div>
          <h2 className="text-display-md mb-4">The conceptual map.</h2>
        </div>
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {[
            { term: "Strategic Sovereignty", href: "/glossary/strategic-sovereignty/", desc: "The operational state the digital conglomerate achieves.", accent: "red" },
            { term: "Penta P's Framework", href: "/glossary/penta-ps-framework/", desc: "The five-domain model that structures the digital conglomerate's capabilities.", accent: "cyan" },
            { term: "Sovereign Operations Architecture", href: "/glossary/sovereign-operations-architecture/", desc: "The six-stage methodology that orchestrates the digital conglomerate.", accent: "emerald" },
            { term: "Operational Excellence", href: "/glossary/operational-excellence/", desc: "The engineered system that produces verified outcomes.", accent: "amber-gold" },
            { term: "eGovernance", href: "/glossary/egovernance/", desc: "The policy pillar operationalized through digital infrastructure.", accent: "lime" },
            { term: "Perception Management", href: "/glossary/perception-management/", desc: "The perception-pillar discipline.", accent: "purple" },
          ].map((t, i) => {
            const a = ACCENT[t.accent];
            return (
              <motion.div
                key={t.href}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-30px" }}
                transition={{ duration: 0.4, delay: i * 0.05 }}
              >
                <Link href={t.href} className="block h-full group">
                  <div className={`relative glass-card rounded-2xl p-5 h-full bg-gradient-to-br ${a.bg} to-transparent border ${a.border} hover:scale-[1.01] transition-all`}>
                    <h3 className={`text-heading-sm mb-2 ${a.text}`}>{t.term}</h3>
                    <p className="text-caption text-muted-foreground leading-relaxed mb-3">{t.desc}</p>
                    <span className={`inline-flex items-center gap-1.5 text-caption font-semibold ${a.text} group-hover:gap-2.5 transition-all`}>
                      Read <ArrowRight className="size-3" />
                    </span>
                  </div>
                </Link>
              </motion.div>
            );
          })}
        </div>
      </Section>

      <Section background="muted" className="py-16 lg:py-20">
        <div className="text-center mb-10">
          <div className={`inline-flex items-center gap-2 px-3 py-1 rounded-full ${ACCENT.cyan.pill} mb-4`}>
            <Network className="size-3.5 text-cyan" />
            <span className="text-overline text-cyan">Conglomerate Structure Map</span>
          </div>
          <h2 className="text-display-md mb-4">The architectural constellation.</h2>
          <p className="text-body-md text-muted-foreground max-w-2xl mx-auto">
            The digital conglomerate is structured as a central command node surrounded by five operational satellites. The constellation maps how intelligence, perception, governance, crisis, and security capabilities coordinate under a single operational architecture.
          </p>
        </div>
        <ArchitectureDiagramBlock
          center={{
            label: "Neural Command Interface",
            body: "Tier 4 orchestration hub. 95% coordination success rate. The apex node of the operational architecture.",
            accent: "amber-gold",
          }}
          satellites={[
            { label: "Intelligence Operations", body: "89% crisis prediction accuracy. 200+ platforms monitored. 10,000+ collection agents globally.", accent: "cyan" },
            { label: "Perception Operations", body: "95-98% organic authenticity rating. 50+ digital platforms. 10,000-1M+ concurrent personas.", accent: "purple" },
            { label: "Governance Operations", body: "127 modules across 9 government functions. Up to 120M citizens per deployment.", accent: "emerald" },
            { label: "Crisis Operations", body: "Sub-5-minute response time. 384x to 1,416x faster than traditional crisis management.", accent: "red" },
            { label: "Security Operations", body: "Zero-trust architecture. 99.9999% uptime. Zero security incidents in 15+ years.", accent: "amber-gold" },
          ]}
          connections={[
            { from: -1, to: 0 }, { from: -1, to: 1 }, { from: -1, to: 2 }, { from: -1, to: 3 }, { from: -1, to: 4 },
            { from: 0, to: 1 }, { from: 1, to: 2 }, { from: 2, to: 3 }, { from: 3, to: 4 }, { from: 4, to: 0 },
          ]}
        />
      </Section>

      <Section background="default" className="py-16 lg:py-20">
        <div className="text-center mb-10">
          <div className={`inline-flex items-center gap-2 px-3 py-1 rounded-full ${ACCENT.denim.pill} mb-4`}>
            <Compass className="size-3.5 text-denim" />
            <span className="text-overline text-denim">Usage Examples</span>
          </div>
          <h2 className="text-display-md mb-4">The digital conglomerate in operation.</h2>
          <p className="text-body-md text-muted-foreground max-w-2xl mx-auto">
            Four examples of the digital conglomerate's operating model in practice. Each example illustrates how the integrated architecture produces sovereign-grade outcomes.
          </p>
        </div>
        <FeatureGridBlock
          features={[
            { title: "Sovereign-Scale Campaign", body: "A national-level political campaign managed through the integrated architecture — intelligence, perception, content, ground truth, crisis response, and security operating in coordinated unity. Eighty-three point three percent success rate across eighteen engagements.", accent: "red" },
            { title: "Multi-Country Service Delivery", body: "Service delivery to clients across eighteen countries on three continents. The integrated architecture coordinates teams, manages data sovereignty requirements, and maintains operational consistency regardless of physical location.", accent: "purple" },
            { title: "Crisis Recovery Engagement", body: "A Fortune-scale corporation under existential reputational threat. The crisis response engine activates 500+ pre-built playbooks within minutes. The recovery is managed through the full integrated architecture, with all five satellites coordinating under the central command.", accent: "cyan" },
            { title: "Royal Family Advisory", body: "Long-term advisory relationship for a royal house. Intelligence, perception, governance transformation, crisis preparedness, and sovereign security — all delivered through the integrated architecture, all under a single operational command.", accent: "emerald" },
          ]}
          columns={2}
        />
      </Section>

      <Section background="muted" className="py-16 lg:py-20">
        <div className="text-center mb-10">
          <div className={`inline-flex items-center gap-2 px-3 py-1 rounded-full ${ACCENT["amber-gold"].pill} mb-4`}>
            <BookOpen className="size-3.5 text-amber-gold" />
            <span className="text-overline text-amber-gold">Common Misconceptions</span>
          </div>
          <h2 className="text-display-md mb-4">What a digital conglomerate is not.</h2>
          <p className="text-body-md text-muted-foreground max-w-2xl mx-auto">
            Six persistent misconceptions that conflate the digital conglomerate with other organizational forms. The distinctions matter — they determine what kind of outcomes the structure can produce.
          </p>
        </div>
        <AccordionBlock
          items={[
            { q: "Is a digital conglomerate the same as a holding company?", a: "No. A holding company owns equity stakes in independent operating companies. A digital conglomerate owns and operates platforms that share data, intelligence, and operational capabilities. The integration is operational, not financial." },
            { q: "Is a digital conglomerate the same as a tech conglomerate?", a: "No. Tech conglomerates like major platform companies operate consumer-facing services at scale. A digital conglomerate operates sovereign-grade services for elite clients. The operating context, client profile, and consequence profile are categorically different." },
            { q: "Can a digital conglomerate be replicated through acquisitions?", a: "No. The integrated capabilities cannot be acquired — they must be built. The operational synergies, the closed-loop data architecture, and the institutional knowledge emerge from years of disciplined co-development. Acquisition produces a portfolio, not a digital conglomerate." },
            { q: "Does a digital conglomerate require a massive workforce?", a: "No. The 70% Operating Model demonstrates that team size is not the determinant of capability. A digital conglomerate built on the 70% composition produces the highest output per operator. Quality of talent, not quantity, is the variable." },
            { q: "Is a digital conglomerate a consulting firm?", a: "No. Consulting firms deliver recommendations. A digital conglomerate delivers outcomes. The distinction is operational, not rhetorical. The architecture is engineered to execute, not to advise." },
            { q: "Can a digital conglomerate exist in any industry?", a: "The digital conglomerate form applies most directly to industries where sovereign-scale integration produces compound value — government, defense, intelligence, political strategy, perception management, and high-stakes corporate operations. Industries without sovereign-scale stakes may benefit from other organizational forms." },
          ]}
        />
      </Section>

      <Section background="default" className="py-20 lg:py-28">
        <div className="relative">
          <div aria-hidden className="absolute -top-20 -left-20 w-96 h-96 rounded-full bg-purple/15 blur-3xl pointer-events-none" />
          <div aria-hidden className="absolute -bottom-20 -right-20 w-96 h-96 rounded-full bg-cyan/15 blur-3xl pointer-events-none" />
          <div className="relative glass-strong rounded-3xl p-8 md:p-12 lg:p-16 gradient-border text-center">
            <Building2 className="size-10 text-purple mx-auto mb-4" />
            <h2 className="text-display-md mb-3">Engage the digital conglomerate.</h2>
            <p className="text-body-lg text-muted-foreground max-w-2xl mx-auto mb-8">
              Not a consulting firm. Not a technology vendor. An integrated strategic systems engine. The digital conglomerate commands outcomes from a unified operational architecture.
            </p>
            <div className="flex flex-col sm:flex-row items-center justify-center gap-3">
              <Link
                href="/contact/"
                className="inline-flex items-center justify-center gap-2 h-12 px-6 rounded-md bg-gradient-sovereign text-white font-semibold shadow-lg hover:scale-105 transition-transform"
              >
                <Mic className="size-4" />
                Begin an Engagement
                <ArrowRight className="size-4" />
              </Link>
              <Link
                href="/about/"
                className="inline-flex items-center justify-center gap-2 h-12 px-6 rounded-md glass font-semibold hover:scale-105 transition-transform"
              >
                <Crown className="size-4" />
                About Dr. Jyoti Kush
              </Link>
            </div>
          </div>
        </div>
      </Section>

      <RelatedPages
        title="Continue exploring"
        items={[
          { title: "Glossary Hub", description: "Ten terms defining sovereign-scale operations.", href: "/glossary/", icon: BookOpen, accent: "cyan" },
          { title: "About Dr. Jyoti Kush", description: "The full story. Physician. Operator. Architect.", href: "/about/", icon: Crown, accent: "red" },
          { title: "FAQ Hub", description: "Eight subpages covering every domain of expertise.", href: "/faq/", icon: Sparkles, accent: "purple" },
          { title: "Platforms FAQ", description: "The nine proprietary platforms in detail.", href: "/faq/platforms/", icon: Layers, accent: "amber-gold" },
        ]}
      />
    </>
  );
}
